import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import openSocket, { refreshSocketIdentity } from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from "../../services/authToken";

let refreshSessionPromise = null;

const refreshSession = async () => {
	if (!refreshSessionPromise) {
		refreshSessionPromise = api
			.post("/auth/refresh_token")
			.then(response => response.data)
			.finally(() => {
				refreshSessionPromise = null;
			});
	}

	return refreshSessionPromise;
};

// Rotas em que 401 NAO significa sessao expirada, e por isso nao podem passar
// pela renovacao automatica. O login e o caso que doia: senha errada devolve
// 401, o interceptor tentava renovar, o refresh falhava por nao haver cookie, e
// o erro DELE substituia o do login - o atendente via "Sessao expirada" no lugar
// de "usuario ou senha invalidos", sem nunca ter tido sessao.
const NO_REFRESH_RETRY_PATHS = [
  "/auth/refresh_token",
  "/auth/login",
  "/auth/signup",
  "/auth/logout"
];

const shouldSkipRefreshRetry = url =>
  NO_REFRESH_RETRY_PATHS.some(path => String(url || "").includes(path));

const getRequestToken = config => {
	const authorization =
		config?.headers?.Authorization || config?.headers?.authorization || "";
	const match = String(authorization).match(/^Bearer\s+(.+)$/i);
	return match ? match[1] : null;
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const presenceSocketRef = useRef(null);

	const companyBlockDefinitions = {
		"Trial expirado. Entre em contato com o suporte.": {
			title: "Acesso bloqueado",
			reason: "Trial expirado",
			description:
				"O período de trial desta empresa terminou. Entre em contato com o suporte para regularizar o acesso.",
		},
		"Conta suspensa. Entre em contato com o suporte.": {
			title: "Acesso bloqueado",
			reason: "Conta suspensa",
			description:
				"Esta empresa está suspensa no momento. Entre em contato com o suporte para reativar o acesso.",
		},
		"Assinatura cancelada.": {
			title: "Acesso indisponível",
			reason: "Assinatura cancelada",
			description:
				"A assinatura desta empresa foi cancelada. Entre em contato com o suporte para reativar a conta.",
		},
		"Empresa inativa.": {
			title: "Acesso indisponível",
			reason: "Empresa inativa",
			description:
				"Esta empresa está inativa no momento. Entre em contato com o suporte para obter ajuda.",
		},
		"Empresa arquivada.": {
			title: "Acesso indisponivel",
			reason: "Empresa arquivada",
			description:
				"Esta empresa foi arquivada e o acesso esta bloqueado. Entre em contato com o suporte se precisar reativar a operacao.",
		},
	};

	const persistBlockedCompany = payload => {
		window.sessionStorage.setItem("companyAccessBlocked", JSON.stringify(payload));
	};

	const clearBlockedCompany = () => {
		window.sessionStorage.removeItem("companyAccessBlocked");
	};

	const getDefaultRedirectPath = currentUser => {
		if (currentUser?.profile === "superadmin") {
			return "/companies";
		}

		return "/";
	};

	const updateCurrentUser = nextUser => {
		setUser(prevUser => {
			const mergedUser = { ...prevUser, ...nextUser };
			return mergedUser;
		});
	};

	const handleBlockedCompany = error => {
		const errorMsg = error?.response?.data?.error;
		const blockedCompany = companyBlockDefinitions[errorMsg];

		if (!blockedCompany) {
			return false;
		}

		clearAccessToken();
		delete api.defaults.headers.Authorization;
		setIsAuth(false);
		setUser({});
		persistBlockedCompany({
			...blockedCompany,
			rawMessage: errorMsg,
		});
		history.push("/login", { blockedCompany });
		return true;
	};

	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(
			config => {
				const token = getAccessToken();
				if (token) {
					config.headers = config.headers || {};
					config.headers.Authorization = `Bearer ${token}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config || {};

				if (
					error?.response?.status === 401 &&
					!originalRequest._retry &&
					!shouldSkipRefreshRetry(originalRequest.url)
				) {
					originalRequest._retry = true;
					const currentToken = getAccessToken();
					const failedRequestToken = getRequestToken(originalRequest);

					// A concurrent request may already have refreshed the session.
					if (
						currentToken &&
						failedRequestToken &&
						currentToken !== failedRequestToken
					) {
						originalRequest.headers = originalRequest.headers || {};
						originalRequest.headers.Authorization = `Bearer ${currentToken}`;
						return api(originalRequest);
					}

					try {
						const data = await refreshSession();

						if (data?.token) {
							setAccessToken(data.token);
							api.defaults.headers.Authorization = `Bearer ${data.token}`;
							setIsAuth(true);
							setUser(data.user);

							return api(originalRequest);
						}
					} catch (refreshError) {
						clearAccessToken();
						delete api.defaults.headers.Authorization;
						setIsAuth(false);
						setUser({});
						return Promise.reject(refreshError);
					}
				}

				if (error?.response?.status === 401) {
					clearAccessToken();
					delete api.defaults.headers.Authorization;
					setIsAuth(false);
					setUser({});
				}

				if (error?.response?.status === 403) {
					handleBlockedCompany(error);
				}

				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, []);

	useEffect(() => {
		let isMounted = true;

		(async () => {
			try {
				const data = await refreshSession();

				if (!isMounted) {
					return;
				}

				setAccessToken(data.token);
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				setIsAuth(true);
				setUser(data.user);
				clearBlockedCompany();
			} catch (err) {
				clearAccessToken();
				delete api.defaults.headers.Authorization;
				handleBlockedCompany(err);
			}

			if (isMounted) {
				setLoading(false);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (!user?.id || !getAccessToken()) {
			return undefined;
		}

		const socket = openSocket();
		presenceSocketRef.current = socket;
		let heartbeatInterval = null;
		let lastActivitySentAt = 0;
		let isRefreshingSocketAuth = false;
		let lastSocketRefreshAttemptAt = 0;

		const syncSocketAuth = () => {
			const latestToken = getAccessToken();
			socket.auth = latestToken ? { token: latestToken } : {};
		};

		const emitActivity = () => {
			const now = Date.now();
			if (now - lastActivitySentAt < 30000) {
				return;
			}

			lastActivitySentAt = now;
			socket.emit("userPresence:activity");
		};

		const emitPresencePulse = () => {
			socket.emit("userPresence:heartbeat");
		};

		const emitPresenceOffline = () => {
			try {
				socket.emit("userPresence:offline");
			} catch (error) {
				// best-effort only
			}
		};

		const handleForceLogout = () => {
			try {
				socket.disconnect();
			} catch (error) {
				// no-op
			}

			if (presenceSocketRef.current === socket) {
				presenceSocketRef.current = null;
			}

			clearAccessToken();
			delete api.defaults.headers.Authorization;
			setIsAuth(false);
			setUser({});
			clearBlockedCompany();
			toast.info(i18n.t("auth.toasts.forcedLogout"));
			history.push("/login");
		};

		const refreshSocketAuth = async () => {
			const now = Date.now();
			if (isRefreshingSocketAuth || now - lastSocketRefreshAttemptAt < 15000) {
				return;
			}

			isRefreshingSocketAuth = true;
			lastSocketRefreshAttemptAt = now;

			try {
				const data = await refreshSession();
				if (!data?.token) {
					return;
				}

				setAccessToken(data.token);
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				syncSocketAuth();
				setIsAuth(true);

				if (data?.user?.id && String(data.user.id) === String(user.id)) {
					updateCurrentUser(data.user);
				}

				if (!socket.connected) {
					socket.connect();
				}
			} catch (error) {
				// request/response interceptors handle invalid refresh sessions
			} finally {
				isRefreshingSocketAuth = false;
			}
		};

		const handleSocketConnect = () => {
			syncSocketAuth();
			emitPresencePulse();
			emitActivity();
		};

		const handleSocketConnectError = () => {
			syncSocketAuth();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				emitActivity();
				emitPresencePulse();
			}
		};

		const handlePageHide = () => {
			emitPresenceOffline();
		};

		const activityEvents = [
			"pointerdown",
			"keydown",
			"mousedown",
			"touchstart",
			"scroll",
			"focus"
		];

		syncSocketAuth();
		if (socket?.io) {
			socket.io.on("reconnect_attempt", syncSocketAuth);
		}
		socket.on("connect_error", handleSocketConnectError);
		socket.on("connect", handleSocketConnect);
		socket.on("session:refreshRequired", refreshSocketAuth);
		socket.on("session:forceLogout", handleForceLogout);

		heartbeatInterval = window.setInterval(() => {
			emitPresencePulse();
		}, 30000);
		emitPresencePulse();
		emitActivity();

		activityEvents.forEach(eventName => {
			window.addEventListener(eventName, emitActivity, { passive: true });
		});
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("pagehide", handlePageHide);
		window.addEventListener("beforeunload", handlePageHide);

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				updateCurrentUser(data.user);
			}
		});

		return () => {
			if (heartbeatInterval) {
				window.clearInterval(heartbeatInterval);
			}
			activityEvents.forEach(eventName => {
				window.removeEventListener(eventName, emitActivity);
			});
			if (socket?.io) {
				socket.io.off("reconnect_attempt", syncSocketAuth);
			}
			socket.off("connect_error", handleSocketConnectError);
			socket.off("connect", handleSocketConnect);
			socket.off("session:refreshRequired", refreshSocketAuth);
			socket.off("session:forceLogout", handleForceLogout);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("pagehide", handlePageHide);
			window.removeEventListener("beforeunload", handlePageHide);
			if (presenceSocketRef.current === socket) {
				presenceSocketRef.current = null;
			}
			socket.disconnect();
		};
	}, [user?.id]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			setAccessToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			clearBlockedCompany();
			toast.success(i18n.t("auth.toasts.success"));
			history.push(getDefaultRedirectPath(data.user));
			setLoading(false);
		} catch (err) {
			if (handleBlockedCompany(err)) {
				setLoading(false);
				return;
			}
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			const activePresenceSocket = presenceSocketRef.current;
			if (activePresenceSocket) {
				try {
					activePresenceSocket.emit("userPresence:offline");
				} catch (error) {
					// best-effort only
				}
			}
			await api.delete("/auth/logout");
			if (activePresenceSocket) {
				try {
					activePresenceSocket.disconnect();
				} catch (error) {
					// no-op
				}
				if (presenceSocketRef.current === activePresenceSocket) {
					presenceSocketRef.current = null;
				}
			}
			setIsAuth(false);
			setUser({});
			clearAccessToken();
			clearBlockedCompany();
			delete api.defaults.headers.Authorization;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const startSupportSession = async companyId => {
		setLoading(true);

		try {
			const { data } = await api.post(`/auth/impersonate/${companyId}`);
			setAccessToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			// The socket still holds the pre-impersonation identity; without this it
			// would keep the superadmin's permissions and every ticket room join
			// would be denied for the whole support session.
			refreshSocketIdentity();
			setUser(data.user);
			setIsAuth(true);
			toast.success("Modo suporte iniciado.");
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const stopSupportSession = async () => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/impersonate/exit");
			setAccessToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			// Same reason as startSupportSession, in reverse: without reconnecting,
			// the socket would stay joined to the customer's rooms after exiting.
			refreshSocketIdentity();
			setUser(data.user);
			setIsAuth(true);
			toast.success("Modo suporte encerrado.");
			history.push("/companies");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return {
		isAuth,
		user,
		loading,
		handleLogin,
		handleLogout,
		startSupportSession,
		stopSupportSession,
		updateCurrentUser
	};
};

export default useAuth;
